import { useState } from 'react';

import Button, { IconButton } from '@components/button/Button';
import { Icon, Switch } from '@components/index';
import { BulkPaymentDiscountType, FormDataType } from '@types';
import { formatCurrencyValue } from '@utils/currencyFormatter';
import { sanitizeNumberInput } from '@utils/generic';

import theme from '../../../../../../themes/colors';

/** Sanitizes input; if numeric value would exceed max, keeps previous and flags. */
const parsePercentWithinMax = (
  input: string,
  max: number,
  previous: string
): { value: string; rejectedOverMax: boolean } => {
  const s = sanitizeNumberInput(input, true);
  if (!s || s === '.') return { value: s, rejectedOverMax: false };

  const head = s.endsWith('.') ? s.slice(0, -1) : s;
  if (head === '') return { value: s, rejectedOverMax: false };

  const num = parseFloat(s.endsWith('.') ? head : s);
  if (Number.isNaN(num)) return { value: s, rejectedOverMax: false };

  if (num > max) {
    return { value: previous, rejectedOverMax: true };
  }
  return { value: s, rejectedOverMax: false };
};

interface BulkPaymentDiscountProps {
  formData: FormDataType;
  bulkPaymentTierRowErrors?: string[];
  onBulkPaymentDiscountEnabledChange: (enabled: boolean) => void;
  onBulkPaymentDiscountDataChange: (rows: BulkPaymentDiscountType[]) => void;
}

interface TierRowProps {
  row: BulkPaymentDiscountType;
  index: number;
  slabError?: string;
  onFieldChange: (
    rowIndex: number,
    field: 'threshold' | 'discount',
    value: string
  ) => void;
  onDelete: (rowIndex: number) => void;
}

const TierRow = ({
  row,
  index,
  slabError,
  onFieldChange,
  onDelete
}: TierRowProps) => {
  const [thresholdLimitMsg, setThresholdLimitMsg] = useState(false);
  const [discountLimitMsg, setDiscountLimitMsg] = useState(false);

  return (
    <div className="rounded-xl border border-cofeeGreyBlue5 bg-white px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="mt-2 shrink-0 text-sm text-cofeeNeutral10">
          When payer pays
        </span>

        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex overflow-hidden rounded-lg border border-cofeeGreyBlue5">
            <input
              type="text"
              inputMode="numeric"
              className="w-20 bg-white px-3 py-2 text-sm text-cofeeNeutral10 outline-none focus:ring-0"
              value={String(row.threshold.value ?? '')}
              onChange={(e) => {
                const prev = String(row.threshold.value ?? '');
                const { value, rejectedOverMax } = parsePercentWithinMax(
                  e.target.value,
                  100,
                  prev
                );
                onFieldChange(index, 'threshold', value);
                setThresholdLimitMsg(rejectedOverMax);
              }}
              onBlur={() => {
                setThresholdLimitMsg(false);
              }}
            />
            <span className="flex items-center border-l border-cofeeGreyBlue5 bg-cofeeGreyBlue1 px-2 text-sm text-cofeeNeutral7">
              %
            </span>
          </div>
          {thresholdLimitMsg && (
            <span className="text-xs text-cofeeRed7">Maximum is 100%.</span>
          )}
        </div>

        <span className="mt-2 shrink-0 text-sm text-cofeeNeutral10">
          or more of total
        </span>

        <span className="mt-2 shrink-0 text-sm text-cofeeNeutral7">→</span>

        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex overflow-hidden rounded-lg border border-cofeeGreyBlue5">
            <input
              type="text"
              inputMode="numeric"
              className="w-20 bg-white px-3 py-2 text-sm text-cofeeNeutral10 outline-none focus:ring-0"
              value={String(row.discount.value ?? '')}
              onChange={(e) => {
                const prev = String(row.discount.value ?? '');
                const { value, rejectedOverMax } = parsePercentWithinMax(
                  e.target.value,
                  80,
                  prev
                );
                onFieldChange(index, 'discount', value);
                setDiscountLimitMsg(rejectedOverMax);
              }}
              onBlur={() => {
                setDiscountLimitMsg(false);
              }}
            />
            <span className="flex items-center border-l border-cofeeGreyBlue5 bg-lightGreen px-2 text-sm font-medium text-primary">
              %
            </span>
          </div>
          {discountLimitMsg && (
            <span className="text-xs text-cofeeRed7">Maximum is 80%.</span>
          )}
        </div>

        <span className="mt-2 shrink-0 text-sm font-medium text-primary">
          discount
        </span>

        <div className="ml-auto self-center">
          <IconButton
            iconName={index === 0 ? 'trash' : 'trash-red-2'}
            variant="transparent"
            size="none"
            className={`m-0 ${
              index === 0 ? 'cursor-not-allowed opacity-40' : ''
            }`}
            handleClick={() => onDelete(index)}
            disabled={index === 0}
          />
        </div>
      </div>
      {!!slabError && (
        <p className="mt-2 text-xs text-cofeeRed7">{slabError}</p>
      )}
    </div>
  );
};

const BulkPaymentDiscount = ({
  formData,
  bulkPaymentTierRowErrors = [],
  onBulkPaymentDiscountEnabledChange,
  onBulkPaymentDiscountDataChange
}: BulkPaymentDiscountProps) => {
  const rows = formData.bulkPaymentDiscountData ?? [];

  const firstRow = rows[0];
  const exampleTotal = Number(formData.amount) || 1000;
  const exampleThresholdPct = Number(firstRow?.threshold.value) || 50;
  const exampleDiscountPct = Number(firstRow?.discount.value) || 10;
  const exampleThresholdAmount = Math.floor(
    (exampleTotal * exampleThresholdPct) / 100
  );
  const { value: formattedTotal } = formatCurrencyValue(exampleTotal);
  const { value: formattedThresholdAmount } = formatCurrencyValue(
    exampleThresholdAmount
  );

  const handleFieldChange = (
    rowIndex: number,
    field: 'threshold' | 'discount',
    value: string
  ) => {
    onBulkPaymentDiscountDataChange(
      rows.map((row, index) =>
        index === rowIndex ? { ...row, [field]: { ...row[field], value } } : row
      )
    );
  };

  const handleDelete = (rowIndex: number) => {
    onBulkPaymentDiscountDataChange(
      rows.filter((_, index) => index !== rowIndex)
    );
  };

  const handleAddTier = () => {
    onBulkPaymentDiscountDataChange([
      ...rows,
      {
        threshold: { type: 'percentage', value: '' },
        discount: { type: 'percentage', value: '' }
      }
    ]);
  };

  return (
    <div>
      <div className="flex items-start gap-2">
        <Switch
          handleClick={onBulkPaymentDiscountEnabledChange}
          value={!!formData.isBulkPaymentDiscountEnabled}
        />
        <div className="min-w-0 flex-1">
          <span className="text-base font-semibold text-cofeeNeutral10">
            Bulk payment discount
          </span>
          <div className="text-sm text-cofeeNeutral7">
            Offer a discount when payers pay a larger portion of their total
            outstanding amount at once.
          </div>
        </div>
      </div>

      {formData.isBulkPaymentDiscountEnabled && (
        <div className="mt-4">
          <p className="mb-3 text-sm text-cofeeNeutral7">
            Configure discount tiers below. The highest qualifying tier applies
            automatically.
          </p>

          <div className="space-y-3">
            {rows.map((row, index) => (
              <TierRow
                key={row.ordering_key ?? `bulk-discount-tier-${index}`}
                row={row}
                index={index}
                slabError={bulkPaymentTierRowErrors[index]}
                onFieldChange={handleFieldChange}
                onDelete={handleDelete}
              />
            ))}
          </div>

          <Button
            variant="secondary"
            label="Add tier"
            handleClick={handleAddTier}
            leftIconName="plus"
            leftIconStroke={theme.primary}
            className="mt-3 w-45"
          />

          <div className="mt-4 flex gap-2 rounded-lg bg-cofeeBlue2 p-4">
            <Icon name="info" className="mt-0.5 !size-4 shrink-0 rotate-180" />
            <span className="text-sm text-cofeeBlue8">
              Example: If a student owes <span className="font-inter">₹</span>
              {formattedTotal} and pays <span className="font-inter">₹</span>
              {formattedThresholdAmount} or more ({exampleThresholdPct}%+), they
              get {exampleDiscountPct}% off the payment amount.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkPaymentDiscount;
